import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import { CheckListModelTaskDTO } from "../../models/CheckListModelTask/CheckListModelTaskDTO";
import { CheckListModelTaskService } from "../../services/CheckListModelTaskService";
import Toast from "../../helpers/Toast";
import { SortingRule } from "react-table";
import { Utilities } from '../../helpers/Utilities';
import TableComponent, { OperationsCell } from '../../components/TableComponent';
import { Parameter } from "../../models/Parameter";
import { useIntl, FormattedMessage } from "react-intl";
import CheckListModelTaskCreateModal from "./CheckListModelTaskCreateModal";
import CheckListModelTaskEditModal from './CheckListModelTaskEditModal';
import CheckListModelTaskDeleteModal from './CheckListModelTaskDeleteModal';

type CheckListModelTasksComponentProps = {
    checkListModelId: string | null,
}

function CheckListModelTasksComponent({ checkListModelId }: CheckListModelTasksComponentProps) {
    const intl = useIntl();
    const navigate = useNavigate();

    const [data, setData] = useState<CheckListModelTaskDTO[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const [goToPage, setGoToPage] = useState<number>();
    const [currentSorting, setCurrentSorting] = useState<SortingRule<object>[] | null>(null);

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const checkListModelTaskService = new CheckListModelTaskService();
    const pageSizes = [5, 10, 15, 30];

    const columns = React.useMemo(() => [
        {
            Header: "",
            accessor: " ",
            Cell: (props: any) => OperationsCell(props.cell.row.original.id, "checkListModelTasks"),
        },
        {
            Header: intl.formatMessage({ id: "Screens.CheckListModelTask.Table.Title" }),
            accessor: "title",
        },
        {
            Header: intl.formatMessage({ id: "Screens.CheckListModelTask.Table.Order" }),
            accessor: "order",
        },
        {
            Header: intl.formatMessage({ id: "Screens.CheckListModelTask.Table.Active" }),
            accessor: "active",
            Cell: ({ value }: { value: boolean }) => <>{value ? "Ativo" : "Inativo"}</>
        },
        {
            Header: intl.formatMessage({ id: "Screens.CheckListModelTask.Edit.Info.Actions" }),
            accessor: "actions",
            disableSortBy: true,
            Cell: ({ row }: any) => (
                <div className="d-flex gap-2 justify-content-center">
                    <ButtonComponent
                        text="Editar"
                        onClick={() => {
                            setSelectedTaskId(row.original.id);
                            setIsEditModalOpen(true);
                        }}
                        style={{ minWidth: "100px" }}
                    />
                    <ButtonComponent
                        text="Eliminar"
                        className="btn btn-sm btn-danger"
                        onClick={() => {
                            setSelectedTaskId(row.original.id);
                            setIsDeleteModalOpen(true);
                        }}
                    />
                </div>
            )
        }
    ], [intl]);

    const fetchData = async (page: number, size: number, sortBy: SortingRule<object>[] | null) => {
        setIsLoading(true);

        const response = await checkListModelTaskService.GetAllTasks(
            Number(checkListModelId),
            [],
            Utilities.MountSortParameters(sortBy),
            page,
            size
        );

        if (response.success) {
            setData(response.items);
            setPageCount(response.totalPages);
            setCurrentPage(page);
            setPageSize(size);
        } else {
            Toast.Show("error", response.message);
        }

        setIsLoading(false);
    };

    useEffect(() => {
        fetchData(currentPage, pageSize, currentSorting);
    }, []);

    return (
        <div className="mt-3">
            <Row className="mb-3">
                <div className="d-flex justify-content-between align-items-center w-100 px-3">
                    <h5><FormattedMessage id="Screens.CheckListModelTask.Title" defaultMessage="Tarefas a realizar" /></h5>
                    <ButtonComponent
                        className="btn-success"
                        text={intl.formatMessage({ id: "Screens.CheckListModelTask.Create" })}
                        icon={<i className="fas fa-plus" />}
                        onClick={() => setIsCreateModalOpen(true)}
                    />
                </div>
            </Row>

            <Row>
                <Col xl={12}>
                    {data.length > 0 ? (
                        <TableComponent
                            goToPage={goToPage}
                            loading={isLoading}
                            pageCount={pageCount}
                            columns={columns}
                            manualPagination
                            fetchData={fetchData}
                            pageSizes={pageSizes}
                            initialPage={1}
                            initialPageSize={10}
                            initialSortBy={[]}
                            data={data}
                            dontOrderFirstCollumn
                        />
                    ) : (
                        !isLoading && (
                            <div className="text-center py-4 text-muted">
                                <FormattedMessage
                                    id="Screens.CheckListModelTask.Empty"
                                    defaultMessage="Este modelo de checklist ainda não tem tarefas."
                                />
                            </div>
                        )
                    )}
                </Col>
            </Row>

            <CheckListModelTaskCreateModal
                isOpen={isCreateModalOpen}
                toggle={() => setIsCreateModalOpen(false)}
                checkListModelId={Number(checkListModelId)}
                onTaskCreated={() => {
                    setIsCreateModalOpen(false);
                    fetchData(currentPage, pageSize, currentSorting);
                }}
            />

            <CheckListModelTaskEditModal
                isOpen={isEditModalOpen}
                toggle={() => setIsEditModalOpen(false)}
                checkListModelId={Number(checkListModelId)}
                taskId={selectedTaskId as number}
                onUpdate={() => {
                    setIsEditModalOpen(false);
                    fetchData(currentPage, pageSize, currentSorting);
                }}
            />
            <CheckListModelTaskDeleteModal
                isOpen={isDeleteModalOpen}
                toggle={() => setIsDeleteModalOpen(false)}
                taskId={selectedTaskId}
                onDeleted={() => {
                    fetchData(currentPage, pageSize, currentSorting);
                    setIsDeleteModalOpen(false);
                }}
            />
        </div>
    );
}

export default CheckListModelTasksComponent;
